import { useState, useEffect } from 'react';
import { useLanguage } from '../../providers/preferenceProvider';
import { useConfig } from '../../providers/configProvider';
import SearchableSelect from './searchableSelect';
import '../../css/overlay.css';
import '../../css/login.css';
import { getCompaniesPlain } from '../../api/company-api';
import { getRoles } from '../../api/role-api';
import { createRegistration } from '../../api/registration-api';
import { updateUser, deleteUser, regenerateUserPersonalCode } from '../../api/user-api';
import { useToast } from "../../providers/toastProvider";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faEnvelope, faUser, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { ACL } from '../../acls';


const EditUser = ({ isOpen, onClose, admin, user, createNew, fixedCompany, onRefresh  }) => {
  const { t } = useLanguage();
  const { isAuthorized, isCompanyManager } = useConfig();
  const { showToast, showActionToast, showLoading, hideLoading } = useToast();
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [credits, setCredits] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);

  const [companyManager, setCompanyManager] = useState(false);

  const [companyOptions, setCompanyOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  const [personalCode, setPersonalCode] = useState();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const companiesResponse = await getCompaniesPlain();
        setCompanyOptions([
          { id: '', name: 'None' },
          ...companiesResponse.data
        ]);

        const rolesResponse = await getRoles();
        if (!admin) {
          setRole(fixedCompany.defaultRoleId);
        } else {
          setRoleOptions(
            rolesResponse.data.map((role) => ({
              id: role.id,
              name: role.name
            }))
          );
        }
      } catch (error) {
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (user) {
      setFirstname(user.firstname);
      setLastName(user.lastname);
      setEmail(user.email);
      setCompany(user.companyId ||  '');
      setCompanyManager(user.companyManager);
      setRole(user.roleId || '');
      setCredits(user.credits || '');
      setPersonalCode(user.personalCode);
    } else if (!user) {
      setFirstname('');
      setLastName('');
      setEmail('');
      if (admin) {
        setCompany('');
        setRole('');
      } else {
        setCompany(fixedCompany.id);
        setRole(fixedCompany.defaultRoleId);
      }
      setCompanyManager(false);
    }
  }, [isOpen, user, companyOptions, admin, fixedCompany]);

  if (!isOpen) return null;

  const closeOverlay = () => {
    setFirstname('');
    setLastName('');
    setEmail('');
    setCompany('');
    setRole('');
    setCompanyManager(false);
    onClose();
  }

  const saveChange = async () => {
    showLoading();

    const newErrors = [];
    if (!firstname || firstname.trim() === '') newErrors.push('firstname');
    if (!lastname || lastname.trim() === '') newErrors.push('lastname');
    if (!email || email.trim() === '') newErrors.push('email');
    if (company && !companyOptions.some(c => c.id === company)) newErrors.push('company');
    if (admin && (!role || !roleOptions.some(r => r.id === role))) newErrors.push('role');
    setFieldErrors(newErrors);

    if (newErrors.length > 0) {
      showToast(t.toast.fill_all_fields, 'error');
      hideLoading();
      return;
    }

    if (createNew) {
      showLoading();
      if (!admin) {
        setCompany(fixedCompany.id);
        setRole(fixedCompany.defaultRoleId);
      }
      createRegistration(firstname?.trim(), lastname?.trim(), email?.trim(), company, role, companyManager).then(() => {
        if (onRefresh) onRefresh();
        onClose();
        hideLoading();
        showToast(t.toast.register_send_message.replace('{ph_name}', email), 'success');
      }).catch((error) => {
        hideLoading();
      });
    } else {
      updateUser(user.id, firstname?.trim(), lastname?.trim(), email?.trim(), company, role, companyManager, credits).then(() => {
        if (onRefresh) onRefresh(true);
        onClose();
        showToast(t.user + " " + t.toast.updated_success, 'success');
        hideLoading();
      }).catch((error) => {
        hideLoading();
      });
    }
  };

  const deleteConfirmation = () => {
    showActionToast(
      t.toast.confirm_delete_general.replace('{ph_name}', user.firstname + ' ' + user.lastname),
      handleDeleteUser,
      null
    );
  }

  const handleDeleteUser = () => {
    showLoading();
    deleteUser(user.id).then(() => {
      if (onRefresh) onRefresh(true);
      hideLoading();
      onClose();
      showToast(t.user + " " + t.toast.deleted_success, 'success');
    }).catch(() => {
      hideLoading();
    });
  }
  
  async function regeneratepc() {
    const updatedUser = await regenerateUserPersonalCode(user.id);
    setPersonalCode(updatedUser.personalCode);
  }

  return (
    <div className="overlay-backdrop w-full h-full" onClick={onClose}>
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">{createNew ? t.add : t.edit} {t.user}</div>
        <div className="overlay-admin-form flex flex-col gap-1 p-4">
          <div className="flex flex-row items-center gap-4">
            <label htmlFor="firstname" className="w-32"><FontAwesomeIcon icon={faPen} /> Firstname</label>
            <input type="text" id="firstname" name="firstname" value={firstname} onChange={(e) => setFirstname(e.target.value)} placeholder={"Firstname..."} className={"overlay-admin-input" + (fieldErrors.includes("firstname") ? " input-error" : "")} />
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="lastname" className="w-32"><FontAwesomeIcon icon={faPen} /> Lastname</label>
            <input type="text" id="lastname" name="lastname" value={lastname} onChange={(e) => setLastName(e.target.value)} placeholder={"Lastname..."} className={"overlay-admin-input" + (fieldErrors.includes("lastname") ? " input-error" : "")} />
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="email" className="w-32"><FontAwesomeIcon icon={faEnvelope} /> {t.email}</label>
            <input type="email" id="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.email + "..."} className={"overlay-admin-input" + (fieldErrors.includes("email") ? " input-error" : "")} />
          </div>

          {admin && (
            <SearchableSelect label={t.company} icon="company" name="company" value={company} onChange={setCompany} options={companyOptions} error={fieldErrors.includes("company")} />
          )}

          {admin && (
            <SearchableSelect label={t.role} icon="role" name="role" value={role} onChange={setRole} options={roleOptions} error={fieldErrors.includes("role")} />
          )}

          { admin && (
            <div className="flex flex-row items-center gap-4">
              <label htmlFor="credits" className="w-32">{t.amount_of} {t.credits}</label>
              <input type="number" id="credits" name="credits" value={credits} onChange={(e) => setCredits(e.target.value)} placeholder={t.amount_of + " " + t.credits + "..."} className={"overlay-admin-input"} />
            </div>
          )}

          {(
            <div className="overlay-checkbox flex flex-row items-center">
              <label htmlFor="companyManager" className="w-32">
                <FontAwesomeIcon icon={faUser} /> {t.company_manager}
              </label>

              <label htmlFor="companyManager" className=''>
                <input
                  type="checkbox" id="companyManager" name="companyManager"
                  checked={companyManager} onChange={(e) => setCompanyManager(e.target.checked)}
                />
              </label>
            </div>
          )}
          {!createNew && admin && (
            <div className="overlay-checkbox flex flex-row items-start gap-4">
              <label htmlFor="personalCode" className="w-32">
                <FontAwesomeIcon icon={faUser} /> Personal code
              </label>
              <div> {personalCode} </div>
              <button onClick={regeneratepc}> 
                <FontAwesomeIcon icon = {faRefresh} />
              </button>
              
            </div>
          )}

        </div>
        <div className="flex flex-row gap-2 justify-between">
          <div className='flex flex-row gap-2 align-items-center'>
            {!createNew && (
              <button className="btn" onClick={deleteConfirmation} disabled={!isAuthorized(ACL.USER) && !isCompanyManager(user)}>{t.delete + " " + t.user}</button>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button className="btn-close" onClick={closeOverlay}>{t.close}</button>
            <button className="btn" onClick={saveChange}>{t.confirm}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUser;